import PropTypes from "prop-types";
import React from "react";
import classNames from "classnames";
import { Text } from "../Text/Text";
import "./TextField.scss";

export class TextField extends React.Component {
  static defaultProps = {
    autoResize: false,
    disabled: false,
    defaultValue: "",
  };

  static propTypes = {
    /** If input should autofocus */
    autoFocus: PropTypes.bool,
    /* If input should resize to fit value content */
    autoResize: PropTypes.bool,
    /** Additional class names for the component */
    className: PropTypes.string,
    /** The default value of the TextField */
    defaultValue: PropTypes.string,
    /** Whether the text field's input can be changed */
    disabled: PropTypes.bool,
    /** Error messaged to be displayed */
    errorMessage: PropTypes.string,
    /** Id associated with component */
    id: PropTypes.any,
    /** Ref object to access and manipulate the underlying input element. */
    inputRef: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.shape({ current: PropTypes.any }),
    ]),
    /** Label associated with component */
    label: PropTypes.string,
    /** Callback function for when TextField's input is changed */
    onChange: PropTypes.func,
    /** Callback function for when the TextField is clicked */
    onClick: PropTypes.func,
    /** Callback function when a key is pressed */
    onKeyDown: PropTypes.func,
    /** Placeholder associated with component */
    placeholder: PropTypes.string,
    /** Callback function when the TextField is focused */
    onFocus: PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.state = {
      inputSize: 20,
      value: props.defaultValue,
    };
  }

  componentDidMount() {
    this.updateInputSize();
  }

  componentDidUpdate(prevProps) {
    const { defaultValue } = this.props;
    this.updateDefaultValueStateIfPropChanged(
      defaultValue,
      prevProps.defaultValue
    );
  }

  updateDefaultValueStateIfPropChanged = (
    newDefaultValue,
    previousDefaultValue
  ) => {
    if (newDefaultValue !== previousDefaultValue) {
      this.setState({ value: newDefaultValue });
    }
  };

  updateInputSize = () => {
    const { value } = this.state;
    const { autoResize } = this.props;

    if (autoResize && value) {
      const inputSize = value.length;
      this.setState({ inputSize });
    }
  };

  onChange = (event) => {
    const { disabled, id, onChange } = this.props;

    const { value } = event.target;

    this.setState({ value });

    if (!disabled) {
      if (onChange) {
        if (id) {
          onChange({
            id,
            value,
          });
        } else {
          onChange(value);
        }
      }
    }
  };

  onClick = (event) => {
    const { onClick } = this.props;

    if (onClick) {
      onClick(event);
    }
  };

  onKeyDown = (event) => {
    const { onKeyDown } = this.props;
    if (onKeyDown) {
      onKeyDown(event);
    }
  };

  onFocus = (event) => {
    const { onFocus } = this.props;
    if (onFocus) {
      onFocus(event);
    }
  };

  render() {
    const { inputSize } = this.state;
    const {
      autoFocus,
      className,
      disabled,
      errorMessage,
      id,
      inputRef,
      label,
      placeholder,
    } = this.props;

    const { value } = this.state;

    const textFieldClass = classNames(
      "textField",
      { "textField--disabled": disabled },
      { "textField--error": errorMessage },
      className
    );
    return (
      <div className={textFieldClass}>
        {errorMessage && (
          <Text className="textField__message">{errorMessage}</Text>
        )}
        {label && <label htmlFor={id}>{label}</label>}
        <input
          id={id}
          className="textField__input"
          type="text"
          tabIndex={0}
          onChange={this.onChange}
          onClick={this.onClick}
          onKeyDown={this.onKeyDown}
          placeholder={placeholder}
          value={value}
          size={inputSize}
          autoFocus={autoFocus}
          onInput={this.updateInputSize}
          onFocus={(e) => {
            e.currentTarget.setSelectionRange(
              e.currentTarget.value.length,
              e.currentTarget.value.length
            );
            this.onFocus(e);
          }}
          ref={inputRef}
        />
      </div>
    );
  }
}

export default TextField;
